import React from 'react';
import { CheckCircle2, XCircle, Link as LinkIcon } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ChaosToOrder from '../../components/ChaosToOrder';
import SEO from '../../components/SEO';
import GradientOverlay from '../../components/GradientOverlay';
import LogomarkPattern from '../../components/LogomarkPattern';

const ERPIntegration = () => {
  const useCases = [
    {
      title: 'Manufacturing Business',
      location: 'Byron Bay',
      description: 'Integrates CRM with ERP to give salespeople real-time visibility into stock levels and production schedules, eliminating customer promises that can\'t be kept.'
    },
    {
      title: 'Professional Services Firm',
      location: 'Brisbane',
      description: 'Automatically converts a "won" deal in the CRM into a new project and client account in the ERP, eliminating manual setup and reducing time to kickoff by days.'
    },
    {
      title: 'E-commerce Wholesaler',
      location: 'Indonesia',
      description: 'Syncs online orders from CRM to ERP for instant inventory updates and streamlined fulfilment, reducing stockouts by 75%.'
    }
  ];

  const costsOfInaction = [
    'Manual data entry between systems leading to errors',
    'Costly mistakes from outdated information',
    'Poor inventory management causing stockouts or overstocking',
    'Inability to get a real-time view of business performance',
    'Wasted hours reconciling data across disconnected systems'
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="ERP Integration Gold Coast"
        description="Seamless ERP integration services on the Gold Coast. Connect your CRM, accounting, inventory, and operations systems for real-time business visibility."
        keywords="ERP Integration Gold Coast, ERP Consultant Gold Coast, ERP Systems Australia, Business Systems Integration Brisbane, ERP CRM Integration Queensland, Enterprise Resource Planning Gold Coast"
        canonical="https://resolvdigital.com.au/services/erp-integration"
      />
      
      {/* Hero Section - DARK with Background Image */}
      <section style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a4447',
        backgroundImage: 'url(https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '120px 40px 80px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 68, 71, 0.9)'
        }} />
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(13, 115, 119, 0.2)',
              border: '1px solid #0D7377',
              borderRadius: '10rem',
              padding: '8px 20px',
              marginBottom: '24px'
            }}>
              <LinkIcon size={20} style={{ color: '#0D7377' }} />
              <span style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0D7377',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Service
              </span>
            </div>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: '#C9A227',
              textTransform: 'uppercase',
              marginBottom: '32px'
            }}>
              Resolve Your Data Silos.<br />Create a Single Source of Truth.
            </h1>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#ffffff'
            }}>
              Connect your front office (sales, marketing) with your back office (finance, inventory, operations) for a seamless flow of information across your entire business.
            </p>
          </div>
        </div>
      </section>

      {/* What is ERP Integration - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                What is CRM/ERP Integration?
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  It's about connecting the systems that run different parts of your business so they speak the same language and share the same data in real-time.
                </p>
                <p style={{ marginBottom: '20px' }}>
                  When your sales team closes a deal in the CRM, that information flows automatically into your ERP. When inventory levels change, your sales team sees it immediately. When a customer pays an invoice, their record updates across all systems.
                </p>
                <p style={{ fontWeight: 600, color: '#C9A227' }}>
                  One business. One truth. Zero manual data shuffling.
                </p>
              </div>
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: '48px',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                Integration Benefits
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'Real-time data synchronization',
                  'Elimination of manual data entry',
                  '360-degree customer view',
                  'Accurate inventory & financial data',
                  'Faster decision-making capability'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    color: '#0a4447'
                  }}>
                    <CheckCircle2 size={24} style={{
                      color: '#0D7377',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Relevant - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Why It's Critical for Growing Businesses
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            maxWidth: '900px',
            margin: '0 auto 48px'
          }}>
            For a growing business, disconnected systems are a major bottleneck. Integration provides a 360-degree view of your business, enabling smarter decisions on everything from inventory management to financial forecasting.
          </p>
        </div>
      </section>

      {/* Cost of Inaction - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            The Cost of Staying Disconnected
          </h2>
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '900px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {costsOfInaction.map((cost, index) => (
                <li key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: '#1a1c1b'
                }}>
                  <XCircle size={24} style={{
                    color: '#dc3545',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <span>{cost}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Real Australian B2B Success Stories
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#b8d8da',
            maxWidth: '800px',
            margin: '0 auto 80px',
            textAlign: 'center'
          }}>
            See how integration is transforming operations for businesses across Australia.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(10, 93, 96, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                borderRadius: '8px',
                padding: '40px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#C9A227';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {useCase.location}
                </div>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C9A227',
                  marginBottom: '16px'
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  color: '#ffffff'
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default ERPIntegration;
